import type React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";

import cookieStorage from "persistence";

import { AUTH_PAGES_ROUTES } from "constants/routes";

import { buildUrlWithPathAndQueryParams } from "_helpers/utils/urlBuilder";
import { useQueryParam } from "hooks/useQueryParam";
import useTokenVerification from "hooks/user/useTokenVerification";

/**
 *  This component contains some logic that will be applied to all routes
 * that cannot be accessed by unauthenticated users.
 *
 *  This will be rendered by a parent route wrapping all the private routes,
 * and ultimately will cause users to be redirected back to the login page
 * in the scenario that no auth token is found in the cookies.
 *
 * @see https://medium.com/@dennisivy/creating-protected-routes-with-react-router-v6-2c4bbaf7bc1c
 */
const PrivateRouteWrapper: React.FC = () => {
  const location = useLocation();
  const authToken = cookieStorage.getToken();
  const redirectPath = useQueryParam("state");

  // Perform periodic token verification, to ensure the current session is not outdated
  useTokenVerification(authToken);

  // If route is private and user hasn't logged in, redirect them to login page
  // In case there the "state" param was present, we need to preserve its value
  if (!authToken) {
    const redirectUrl = redirectPath
      ? buildUrlWithPathAndQueryParams(AUTH_PAGES_ROUTES.LOG_IN, {}, { state: redirectPath })
      : AUTH_PAGES_ROUTES.LOG_IN;
    return <Navigate to={redirectUrl} state={{ from: location }} replace />;
  }

  // In any other case, render actual content of the route
  return <Outlet />;
};

export default PrivateRouteWrapper;
